import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Drawer } from "@/components/ui/drawer";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableLoading } from "@/components/ui/table";
import { Route as PurchaseOrderListRoute } from "@/routes/(pharma)/_layout/reports/purchase-order-list";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { tableHeadClass, tableCellClass} from "@/components/ui/table-styles";
import { cn } from "@/lib/utils";
import PaginationButton from "@/components/ui/pagination-button";
import { DoubleArrowLeftIcon, DoubleArrowRightIcon, ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { PurchaseOrder } from "@/gen/purchase_order/common_pb";
import { usePurchaseOrderData } from "../-hooks/usePurchaseInvoiceData";
import React from "react";
import { convertToCurrencyRounded } from "@/utils/currency_converter";

// TableCell and TableHead wrappers for consistent styling
const StyledTableCell = ({ className = "", ...props }) => (
  <TableCell className={cn(tableCellClass, "font-medium", className)} {...props} />
);
const StyledTableHead = ({ className = "", ...props }) => (
  <TableHead className={cn(tableHeadClass, className)} {...props} />
);

const columns = [
    { key: "order_number", label: "Purchase Order No." },
    { key: "total_products", label: "Products" },
    { key: "supplier_name", label: "Supplier Name" },
    { key: "contact_person", label: "Contact Person" },
    { key: "phone_number", label: "Phone Number" },
  ];
  const visibleColumns = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>);

export default function Component() {
    const { data, isLoading, error, isFetching } = usePurchaseOrderData();
    const purchaseOrders = data?.purchaseOrders || [];
    const totalCount = data?.totalCount || 0;    
    const searchParams = PurchaseOrderListRoute.useSearch();
    const navigate = useNavigate();

    const [drawerData, setDrawerData] = useState<PurchaseOrder>()
    const router = useRouter();

    const totalPages = data?.totalCount ? Math.ceil(data.totalCount / searchParams.pageSize) : 1;

    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    const toggleRowExpansion = (rowKey: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowKey)) {
        newExpanded.delete(rowKey);
    } else {
        newExpanded.add(rowKey);
    }
    setExpandedRows(newExpanded);
    };

    return (
        <>
            <div className="flex items-center justify-between">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink
                                onClick={() => {
                                    navigate({
                                        to: "/invoicing",
                                    });
                                }}
                                style={{
                                    cursor: "pointer",
                                }}
                            >
                                Reports Overview
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>Purchase Order List</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>
            <hr className="my-4" />
            <div className="mt-4 border rounded-lg bg-white overflow-hidden">
            {isLoading ? (
                <TableLoading columns={columns} visibleColumns={visibleColumns} />
            ) : !data?.purchaseOrders || data.purchaseOrders.length === 0 ? (
                <div className="flex items-center justify-center p-8">
                <div className="text-sm text-gray-500">No entries found</div>
                </div>
            ) : (
                <>
                <Table className="text-sm">
                <TableHeader>
                <TableRow className="hover:bg-inherit">
                    <StyledTableHead rowSpan={2}>Purchase Order No.</StyledTableHead>
                    <StyledTableHead rowSpan={2}>Products</StyledTableHead>
                    <StyledTableHead rowSpan={1} colSpan={3} className="text-center">Supplier Info</StyledTableHead>
                </TableRow>
                <TableRow className="hover:bg-inherit">
                    <StyledTableHead className="text-center">Supplier Name</StyledTableHead>
                    <StyledTableHead className="text-center">Contact Person</StyledTableHead>
                    <StyledTableHead className="text-center">Phone Number</StyledTableHead>

                </TableRow>
                
                </TableHeader>
                <TableBody>
                {purchaseOrders.map((item, index) => {
                    const rowKey = `po-${item.id}`;
                    const isExpanded = expandedRows.has(rowKey);
                    const isLastRow = index === purchaseOrders.length - 1;
                    
                    return (
                    <React.Fragment key={item.id}>
                        <TableRow className="font-medium">
                        <StyledTableCell 
                            onClick={() => {
                            router.navigate({
                                to: "/inventory/purchase-order/$id",
                                params: { id: item.id },
                                search: {
                                index,
                                total: data.totalCount,
                                supplierId: 'all',
                                stockPointId: 'all',
                                }
                            })
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            {item.orderNumber}
                        </StyledTableCell>
                        <StyledTableCell 
                            onClick={(e: { stopPropagation: () => void; }) => {
                            e.stopPropagation();
                            toggleRowExpansion(rowKey);
                            }}
                        >
                            <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors">
                            <svg 
                                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            <span>{item.items.length}</span>
                            </div>
                        </StyledTableCell>
                        <StyledTableCell 
                            className={cn("text-center")}
                            onClick={() => {
                            router.navigate({
                                to: "/inventory/purchase-order/$id",
                                params: { id: item.id },
                                search: {
                                index,
                                total: data.totalCount,
                                supplierId: 'all',
                                stockPointId: 'all',
                                }
                            })
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            {item.supplierName}
                        </StyledTableCell>
                        <StyledTableCell 
                            className={cn("text-center")}
                            onClick={() => {
                            router.navigate({
                                to: "/inventory/purchase-order/$id",
                                params: { id: item.id },
                                search: {
                                index,
                                total: data.totalCount,
                                supplierId: 'all',
                                stockPointId: 'all',
                                }
                            })
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            {item.supplierMetadata?.contactPersonName || "N/A"}
                        </StyledTableCell>
                        <StyledTableCell 
                            className={cn("text-center")}
                            onClick={() => {
                            router.navigate({
                                to: "/inventory/purchase-order/$id",
                                params: { id: item.id },
                                search: {
                                index,
                                total: data.totalCount,
                                supplierId: 'all',
                                stockPointId: 'all',
                                }
                            })
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            {item.supplierMetadata?.phoneNumber || "N/A"}
                        </StyledTableCell>
                        </TableRow>
                        
                        {isExpanded && (
                        <TableRow className="bg-gray-50">
                            <StyledTableCell 
                            colSpan={5}
                            >
                            <div className="py-2 px-4">
                                <div className="text-sm font-semibold text-gray-700 mb-3">Items in Order</div>
                                <div className="grid gap-2">
                                {item.items && item.items.length > 0 ? (
                                    item.items.map((orderItem: { tradeName: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; unit: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; unitType: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined; quantity: number; bonusQuantity: number; unitPrice: any; }, itemIdx: number) => (
                                    <div 
                                        key={itemIdx}
                                        className="flex items-center justify-between py-2 px-3 bg-white rounded-md border"
                                    >
                                        <div className="flex items-center gap-3">
                                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 font-semibold text-xs">
                                            {itemIdx + 1}
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium">{orderItem.tradeName}</span>
                                            <span className="text-xs text-gray-500 ml-2">
                                            ({orderItem.unit} {orderItem.unitType})
                                            </span>
                                        </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                        <span className="text-sm text-gray-600">
                                            Qty: <span className="font-semibold">{Math.floor(orderItem.quantity)}</span>
                                        </span>
                                        {orderItem.bonusQuantity > 0 && (
                                            <span className="text-sm text-green-600">
                                            Bonus: <span className="font-semibold">{Math.floor(orderItem.bonusQuantity)}</span>
                                            </span>
                                        )}
                                        <span className="text-sm text-gray-600">
                                            Price: <span className="font-semibold">{convertToCurrencyRounded(orderItem.unitPrice)}</span>
                                        </span>
                                        </div>
                                    </div>
                                    ))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500">
                                    No items found
                                    </div>
                                )}
                                </div>
                            </div>
                            </StyledTableCell>
                        </TableRow>
                        )}
                    </React.Fragment>
                    );
                })}
                </TableBody>
                </Table>
                </>
                )}
                <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
                <p>{data?.purchaseOrders && data.purchaseOrders.length > 0 ? totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
                    <div className="flex items-center gap-1">
                        <PaginationButton
                            onClick={() => {
                                navigate({
                                    search: {
                                        ...searchParams,
                                        page: 1
                                    }
                                });
                            }}
                            disabled={searchParams.page === 1}
                        >
                            <DoubleArrowLeftIcon className="h-4 w-4" />
                        </PaginationButton>
                        <PaginationButton
                            onClick={() => {
                                navigate({
                                    search: {
                                        ...searchParams,
                                        page: searchParams.page - 1
                                    }
                                });
                            }}
                            disabled={searchParams.page === 1}
                        >
                            <ChevronLeftIcon className="h-4 w-4" />
                        </PaginationButton>
                        {(() => {
                            // TODO: Replace with backend-provided full_count for real pagination

                            const getPageNumbers = () => {
                                const pages = [];
                                if (totalPages <= 7) {
                                    for (let i = 1; i <= totalPages; i++) {
                                        pages.push(i);
                                    }
                                } else {
                                    pages.push(1);
                                    if (searchParams.page > 3) pages.push('...');
                                    for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
                                        pages.push(i);
                                    }
                                    if (searchParams.page < totalPages - 2) pages.push('...');
                                    pages.push(totalPages);
                                }
                                return pages;
                            };
                            return getPageNumbers().map((p, idx) => (
                                p === '...'
                                    ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                                    : <PaginationButton
                                        key={p}
                                        active={searchParams.page === p}
                                        onClick={() => {
                                            navigate({
                                                search: {
                                                    ...searchParams,
                                                    page: typeof p === 'number' ? p : searchParams.page
                                                }
                                            });
                                        }}
                                    >
                                        <span className="text-sm">{p}</span>
                                    </PaginationButton>
                            ));
                        })()}
                        <PaginationButton
                            onClick={() => {
                                navigate({
                                    search: {
                                        ...searchParams,
                                        page: searchParams.page + 1
                                    }
                                });
                            }}
                            disabled={searchParams.page >= totalPages}
                        >
                            <ChevronRightIcon className="h-4 w-4" />
                        </PaginationButton>
                        <PaginationButton
                            onClick={() => {
                                navigate({
                                    search: {
                                        ...searchParams,
                                        page: totalPages
                                    }
                                });
                            }}
                            disabled={searchParams.page >= totalPages}
                        >
                            <DoubleArrowRightIcon className="h-4 w-4" />
                        </PaginationButton>
                    </div>
                </div>
            </div>
        </>
    );
}